// Phase 2.1 — REST-backed tenant service.
//
// Drop-in replacement for the Firestore tenantService when
// REACT_APP_AUTH_BACKEND=api. The exported method names + return shapes
// match `tenantService.js` so consumers don't change.
//
// referralService and userService callouts in `createTenantWithOwner` are
// performed server-side by POST /api/tenants now — the V2 helper just
// sends one request and returns the resulting tenantId.

import { getAccessToken } from '../contexts/AuthContextV2';

const API_BASE = process.env.REACT_APP_API_URL ?? '';

async function apiFetch(path, options = {}) {
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include'
  });
  let body = null;
  try { body = await res.json(); } catch { /* non-json */ }
  if (!res.ok) {
    const err = new Error(body?.error || `Request failed: ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

export const slugifyTenantName = (name) => {
  if (!name) return '';
  return String(name)
    .trim()
    .toLowerCase()
    .replace(/[ً-ٰٟ]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9؀-ۿ-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'tenant';
};

export const tenantService = {
  async getTenant(tenantId) {
    if (!tenantId) return null;
    try {
      const { tenant } = await apiFetch(`/api/tenants/${encodeURIComponent(tenantId)}`);
      return { id: tenant.id, ...tenant };
    } catch (err) {
      if (err.status === 404) return null;
      console.warn('tenantServiceV2.getTenant failed:', err.message);
      return null;
    }
  },

  async upsertTenant(tenantId, data = {}) {
    if (!tenantId) throw new Error('upsertTenant: tenantId is required');
    await apiFetch(`/api/tenants/${encodeURIComponent(tenantId)}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
    return tenantId;
  },

  async createTenantWithOwner({
    tenantName,
    location = '',
    displayName = '',
    referredByCode = null
    // ownerUid/email/phone are derived from the JWT subject server-side
  }) {
    if (!tenantName) throw new Error('createTenantWithOwner: tenantName required');
    const { tenant } = await apiFetch('/api/tenants', {
      method: 'POST',
      body: JSON.stringify({ tenantName, location, displayName, referredByCode })
    });
    return tenant.id;
  },

  async listUsersForTenant(tenantId) {
    if (!tenantId) return [];
    try {
      const { users } = await apiFetch(`/api/tenants/${encodeURIComponent(tenantId)}/users`);
      return users.map((u) => ({ id: u.id, ...u }));
    } catch (err) {
      console.warn('tenantServiceV2.listUsersForTenant failed:', err.message);
      return [];
    }
  },

  async updateBranding(tenantId, patch = {}) {
    if (!tenantId) throw new Error('updateBranding: tenantId required');
    await apiFetch(`/api/tenants/${encodeURIComponent(tenantId)}/branding`, {
      method: 'PATCH',
      body: JSON.stringify(patch)
    });
    return tenantId;
  }
};

export default tenantService;
